import type { MultiPolygon as GeoJsonMultiPolygon, Polygon as GeoJsonPolygon } from 'geojson';
import { memo } from 'react';

import type { ArShapeFeature, ArShapesResponse } from '~api/services';
import { Polygon } from '../components';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type ArShapesLayerProps = {
  /** AR shapes GeoJSON feature collection */
  arShapes: ArShapesResponse;
  /** Callback when an AR shape is clicked */
  onShapeClick: (feature: ArShapeFeature) => void;
  /** Currently selected shape ID for visual highlight */
  selectedShapeId?: string | null;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const AR_SHAPE_STROKE_WIDTH = 2.5;
const SELECTED_AR_SHAPE_STROKE_WIDTH = 3.5;
const AR_SHAPE_HIT_STROKE_WIDTH = 12;
const AR_SHAPE_STROKE_DASHARRAY = '6 3';

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/** Normalises Polygon and MultiPolygon geometries into a flat array of Polygons for rendering. */
const getFeaturePolygons = (feature: ArShapeFeature): GeoJsonPolygon[] =>
  feature.geometry.type === 'MultiPolygon'
    ? (feature.geometry as GeoJsonMultiPolygon).coordinates.map((coords) => ({
        type: 'Polygon' as const,
        coordinates: coords,
      }))
    : [feature.geometry as GeoJsonPolygon];

const getShapeId = (feature: ArShapeFeature) => String(feature.properties.OBJECTID);

/* -------------------------------------------------------------------------- */
/*                              Shape sub-component                           */
/* -------------------------------------------------------------------------- */

type ArShapeProps = {
  feature: ArShapeFeature;
  isSelected: boolean;
  onShapeClick: (feature: ArShapeFeature) => void;
};

const ArShape = memo(({ feature, isSelected, onShapeClick }: ArShapeProps) => {
  const polygons = getFeaturePolygons(feature);

  const handleClick = () => onShapeClick(feature);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: <g> with role="button" for SVG accessibility
    <g
      className="ar-shape group focus:outline-0"
      data-item-id={getShapeId(feature)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`AR Permit: ${feature.properties.Title || feature.properties.AR_Type}`}
    >
      {polygons.map((polygon, i) => (
        <g
          // biome-ignore lint/suspicious/noArrayIndexKey: Stable sub-polygon order within a feature
          key={i}
        >
          <Polygon
            geometry={polygon}
            fill="none"
            stroke="transparent"
            strokeWidth={AR_SHAPE_HIT_STROKE_WIDTH}
            vectorEffect="non-scaling-stroke"
            pointerEvents="stroke"
            className="cursor-pointer"
            aria-hidden="true"
          />
          <Polygon
            geometry={polygon}
            fill="none"
            stroke={isSelected ? 'var(--color-orange-600)' : 'var(--color-orange-500)'}
            strokeWidth={isSelected ? SELECTED_AR_SHAPE_STROKE_WIDTH : AR_SHAPE_STROKE_WIDTH}
            strokeDasharray={AR_SHAPE_STROKE_DASHARRAY}
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
            className="transition-[stroke] duration-150 ease-in-out group-hover:stroke-orange-600 group-focus:stroke-orange-600"
          />
        </g>
      ))}
    </g>
  );
});
ArShape.displayName = 'ArShape';

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer to render AR permit shape outlines on the canvas.
 * Supports click interaction for opening a details drawer.
 *
 * Wrapped in `memo` to avoid re-rendering on every D3 zoom/pan event triggered
 * by the parent canvas. The `arShapes` reference is stable between zooms
 * since it comes from the TanStack Query cache.
 */
const ArShapesLayer = memo(({ arShapes, onShapeClick, selectedShapeId }: ArShapesLayerProps) => {
  if (!arShapes.features.length) return null;

  return (
    <g className="ar-shapes" aria-label="AR Permits layer">
      {arShapes.features.map((feature) => (
        <ArShape
          key={feature.properties.OBJECTID}
          feature={feature}
          isSelected={getShapeId(feature) === selectedShapeId}
          onShapeClick={onShapeClick}
        />
      ))}
    </g>
  );
});
ArShapesLayer.displayName = 'ArShapesLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { ArShapesLayer, type ArShapesLayerProps };
